/**
 * react-select style objects shared by the audit form's selects.
 *
 * Extracted verbatim from DocumentFormModal so the same look applies to every select
 * in the app, not just the audit form. Paired CSS hooks (.audit-form-select__*,
 * .audit-material-select__*, --audit-r-input) live in src/styles/formEnhancements.css.
 */

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 12,
    borderColor: state.isFocused ? '#1a767e' : '#d8e1ec',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(26, 118, 126, 0.12)' : 'none',
    backgroundColor: '#fff',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#1a767e',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '4px 10px',
    gap: 4,
  }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 999,
    backgroundColor: 'rgba(26, 118, 126, 0.12)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#0f4d54',
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#0f4d54',
    borderRadius: 999,
    ':hover': {
      backgroundColor: 'rgba(26, 118, 126, 0.22)',
      color: '#08343a',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6b7787',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: 'hidden',
  }),
};

// Shared wrapping/menu rules for every react-select in this file. A native <select>
// renders its popup as an OS widget, so CSS cannot wrap long option text -- values like
// "Recommend Continue to Next Cycle Without Certificate" or "Major Area of concern for
// Stage II" were simply clipped in their column.
const selectWrapStyles = {
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.4,
    position: 'static',
    transform: 'none',
    maxWidth: '100%',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  option: (base, state) => ({
    ...base,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    backgroundColor: state.isSelected
      ? '#1a767e'
      : state.isFocused ? 'rgba(26, 118, 126, 0.08)' : 'transparent',
    color: state.isSelected ? '#fff' : '#212529',
  }),
};

// Full-width form field. Sized for a normal field rather than the narrow table cell
// materialSelectStyles targets, so the menu tracks the control width instead of forcing
// a 260px minimum.
const formFieldSelectStyles = {
  ...selectWrapStyles,
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 'var(--audit-r-input, 8px)',
    borderColor: state.isFocused ? '#1a767e' : '#d8e1ec',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(26, 118, 126, 0.12)' : 'none',
    backgroundColor: state.isDisabled ? '#e9ecef' : '#fff',
    ':hover': { borderColor: '#1a767e' },
  }),
  menu: (base) => ({ ...base, borderRadius: 'var(--audit-r-input, 8px)' }),
  option: (base, state) => ({ ...selectWrapStyles.option(base, state), padding: '8px 12px' }),
};

// Material / scope cell styling. Two things a native <select> cannot do and which
// matter here: the option text must WRAP (a browser renders the <option> popup itself,
// so CSS cannot touch it -- long scope names like "Sustainability / EU / Certification
// #Indonesia" were simply clipped), and the menu must be able to grow wider than the
// narrow table cell it is anchored to.
const materialSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 0,
    borderRadius: 'var(--audit-r-input, 8px)',
    borderColor: state.isFocused ? '#1a767e' : '#d8e1ec',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(26, 118, 126, 0.12)' : 'none',
    fontSize: '0.82rem',
    ':hover': { borderColor: '#1a767e' },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 6px' }),
  // The selected value wraps too, so the cell grows instead of truncating with an
  // ellipsis the operator cannot read.
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.4,
    position: 'static',
    transform: 'none',
    maxWidth: '100%',
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '0.82rem' }),
  indicatorsContainer: (base) => ({ ...base, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (base) => ({ ...base, color: '#98a4b3', fontSize: '0.8rem' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  // Anchored to the cell but allowed to be wider, so a long option is readable
  // instead of being squeezed into a 130px column.
  menu: (base) => ({
    ...base,
    borderRadius: 'var(--audit-r-input, 8px)',
    minWidth: 260,
    width: 'max-content',
    maxWidth: 420,
  }),
  option: (base, state) => ({
    ...base,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    fontSize: '0.82rem',
    padding: '7px 10px',
    backgroundColor: state.isSelected
      ? '#1a767e'
      : state.isFocused ? 'rgba(26, 118, 126, 0.08)' : 'transparent',
    color: state.isSelected ? '#fff' : '#212529',
  }),
};

export {
  reactSelectStyles,
  selectWrapStyles,
  formFieldSelectStyles,
  materialSelectStyles,
};
